import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { format, parseISO } from 'date-fns';
import { da, enUS } from 'date-fns/locale';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Plus, 
  Calendar, 
  Clock,
  Trash2,
  X,
  Edit3,
  CalendarIcon,
  Repeat,
  FileText
} from 'lucide-react';

const DAYS = [
  { key: 'mon', da: 'Man', en: 'Mon' },
  { key: 'tue', da: 'Tir', en: 'Tue' },
  { key: 'wed', da: 'Ons', en: 'Wed' },
  { key: 'thu', da: 'Tor', en: 'Thu' },
  { key: 'fri', da: 'Fre', en: 'Fri' },
  { key: 'sat', da: 'Lør', en: 'Sat' },
  { key: 'sun', da: 'Søn', en: 'Sun' }
];

const EditableTimeSlot = ({ slot, onSave }) => {
  const [showModal, setShowModal] = useState(false);
  const [time, setTime] = useState(slot.time);
  
  const openModal = () => {
    setTime(slot.time);
    setShowModal(true);
  };
  
  const save = () => {
    if (time !== slot.time) {
      onSave(slot, time);
    }
    setShowModal(false);
  };
  
  return (
    <>
      <div 
        className="glass-card p-4 flex items-center gap-3 cursor-pointer hover:border-emerald-500/40 transition-all"
        onClick={openModal}
        data-testid={`timeslot-${slot.slot_id}`}
      >
        <Clock className="w-5 h-5 text-emerald-400 shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{slot.name}</p>
          <p className="text-sm text-zinc-400">{slot.time}</p>
        </div>
      </div>
      
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
          <div className="glass-card w-full max-w-xs p-6 animate-fade-in" onClick={e => e.stopPropagation()} data-testid={`timeslot-modal-${slot.slot_id}`}>
            <h3 className="text-lg font-semibold mb-1">{slot.name}</h3>
            <p className="text-sm text-zinc-400 mb-4">Rediger tidspunkt</p>
            <input
              type="time"
              value={time}
              onChange={e => setTime(e.target.value)}
              className="input-field w-full text-center text-xl py-3 mb-4"
              autoFocus
              data-testid={`timeslot-input-${slot.slot_id}`}
            />
            <div className="flex gap-3">
              <button onClick={() => setShowModal(false)} className="btn-secondary flex-1">Annuller</button>
              <button onClick={save} className="btn-primary flex-1" data-testid={`timeslot-save-${slot.slot_id}`}>Gem</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const DayDoseSelector = ({ dayDoses, onChange, language, medicineDosage }) => {
  const extractDosageInfo = (dosageStr) => {
    if (!dosageStr) return null;
    const match = dosageStr.match(/(\d+(?:[.,]\d+)?)\s*(mg|g|mcg|µg)/i);
    if (!match) return null;
    const value = parseFloat(match[1].replace(',', '.'));
    const unit = match[2];
    return { value, unit };
  };

  const dosageInfo = extractDosageInfo(medicineDosage);

  const toggleDay = (dayKey) => {
    const newDoses = { ...dayDoses };
    if (newDoses[dayKey]) {
      delete newDoses[dayKey];
    } else {
      newDoses[dayKey] = { whole: 1, half: 0 };
    }
    onChange(newDoses);
  };

  const updateDose = (dayKey, field, value) => {
    const newDoses = { ...dayDoses };
    if (newDoses[dayKey]) {
      newDoses[dayKey] = { ...newDoses[dayKey], [field]: Math.max(0, parseInt(value) || 0) };
    }
    onChange(newDoses);
  };

  const calculateTotalDosage = (dose) => {
    if (!dosageInfo || !dose) return null;
    const pills = (dose.whole || 0) + (dose.half || 0) * 0.5;
    const total = dosageInfo.value * pills;
    return { value: total % 1 === 0 ? total : parseFloat(total.toFixed(1)), unit: dosageInfo.unit };
  };

  return (
    <div className="space-y-2">
      {DAYS.map(day => {
        const isActive = !!dayDoses[day.key];
        const dose = dayDoses[day.key];
        const totalDosage = calculateTotalDosage(dose);
        
        return (
          <div key={day.key} className={`rounded-xl border transition-all ${
            isActive ? 'bg-zinc-800/50 border-emerald-500/30' : 'bg-zinc-900/30 border-zinc-800'
          }`}>
            <div 
              className="flex items-center justify-between p-3 cursor-pointer"
              onClick={() => toggleDay(day.key)}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-medium ${
                  isActive ? 'bg-emerald-500 text-white' : 'bg-zinc-800 text-zinc-400'
                }`}>
                  {language === 'da' ? day.da : day.en}
                </div>
                {isActive && totalDosage && (
                  <span className="text-emerald-400 font-medium">{totalDosage.value}{totalDosage.unit}</span>
                )}
              </div>
              {isActive && (
                <div className="text-sm text-zinc-400">
                  {dose.whole || 0} + {dose.half || 0}½
                </div>
              )}
            </div>
            
            {isActive && (
              <div className="px-3 pb-3 flex gap-3" onClick={e => e.stopPropagation()}>
                <div className="flex-1">
                  <label className="block text-xs text-zinc-500 mb-1">
                    {language === 'da' ? 'Hele' : 'Whole'}
                  </label>
                  <input
                    type="number"
                    value={dose.whole || 0}
                    onChange={e => updateDose(day.key, 'whole', e.target.value)}
                    className="input-field text-center py-2"
                    min="0"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-zinc-500 mb-1">
                    {language === 'da' ? 'Halve' : 'Half'}
                  </label>
                  <input
                    type="number"
                    value={dose.half || 0}
                    onChange={e => updateDose(day.key, 'half', e.target.value)}
                    className="input-field text-center py-2"
                    min="0"
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


const OrdDatePicker = ({ value, onChange, locale, testId }) => {
  const [open, setOpen] = useState(false);
  const dateValue = value ? parseISO(value) : null;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`input-field w-full text-left flex items-center justify-between ${!value ? 'text-zinc-500' : ''}`}
          data-testid={testId}
        >
          {value ? format(dateValue, 'd. MMM yyyy', { locale }) : '—'}
          {value && (
            <span onClick={e => { e.stopPropagation(); onChange(null); }} className="text-zinc-500 hover:text-red-400 ml-2">
              <X className="w-4 h-4" />
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700 z-[70]" align="start">
        <CalendarComponent
          mode="single"
          selected={dateValue}
          onSelect={date => { onChange(date ? format(date, 'yyyy-MM-dd') : null); setOpen(false); }}
          locale={locale}
          className="rounded-md"
        />
      </PopoverContent>
    </Popover>
  );
};

export const Schedule = () => {
  const { t, language, medicines, timeSlots, schedule, addScheduleEntry, deleteScheduleEntry, updateScheduleEntry, updateTimeSlot, loading } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showOrdination, setShowOrdination] = useState(false);
  const locale = language === 'da' ? da : enUS;
  const [formData, setFormData] = useState({
    medicine_id: '',
    slot_id: '',
    day_doses: {},
    special_ordination: null
  });
  const [ordData, setOrdData] = useState({
    start_date: null,
    end_date: null,
    repeat: 'daily',
    time_of_day_id: '',
    whole: 1,
    half: 0
  });
  
  const resetForm = () => {
    setFormData({
      medicine_id: '',
      slot_id: '',
      day_doses: {},
      special_ordination: null
    });
    setOrdData({
      start_date: null,
      end_date: null,
      repeat: 'daily',
      time_of_day_id: '',
      whole: 1,
      half: 0
    });
    setEditingEntry(null);
    setShowForm(false);
    setShowOrdination(false);
  };
  
  const handleEdit = (entry) => {
    setFormData({
      medicine_id: entry.medicine_id,
      slot_id: entry.slot_id,
      day_doses: entry.day_doses || {},
      special_ordination: entry.special_ordination || null
    });
    setEditingEntry(entry);
    setShowForm(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingEntry) {
        await updateScheduleEntry(editingEntry.entry_id, {
          day_doses: formData.day_doses,
          special_ordination: formData.special_ordination
        });
      } else {
        await addScheduleEntry({
          medicine_id: formData.medicine_id,
          slot_id: formData.slot_id,
          day_doses: formData.special_ordination ? {} : formData.day_doses,
          special_ordination: formData.special_ordination
        });
      }
      resetForm();
    } catch (err) {
      console.error(err);
    }
  };
  
  const handleDelete = async (entryId) => {
    try {
      await deleteScheduleEntry(entryId);
    } catch (err) {
      console.error(err);
    }
  };

  const formatDayDoses = (dayDoses, medicineDosage) => {
    if (!dayDoses || Object.keys(dayDoses).length === 0) return '';
    
    const extractDosageInfo = (str) => {
      const match = str?.match(/(\d+(?:[.,]\d+)?)\s*(mg|g|mcg|µg)/i);
      if (!match) return null;
      const val = parseFloat(match[1].replace(',', '.'));
      return { value: val, unit: match[2] };
    };
    
    const dosageInfo = extractDosageInfo(medicineDosage);
    const dayLabels = language === 'da' 
      ? { mon: 'Man', tue: 'Tir', wed: 'Ons', thu: 'Tor', fri: 'Fre', sat: 'Lør', sun: 'Søn' }
      : { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };
    
    // Group by dose
    const doseGroups = {};
    Object.entries(dayDoses).forEach(([day, dose]) => {
      const pills = (dose.whole || 0) + (dose.half || 0) * 0.5;
      const totalVal = dosageInfo ? dosageInfo.value * pills : null;
      const key = totalVal ? `${totalVal % 1 === 0 ? totalVal : totalVal.toFixed(1)}${dosageInfo.unit}` : `${pills}p`;
      if (!doseGroups[key]) doseGroups[key] = [];
      doseGroups[key].push(dayLabels[day]);
    });
    
    return Object.entries(doseGroups)
      .map(([dose, days]) => `${days.join('/')}: ${dose}`)
      .join(' · ');
  };
  
  // Group schedule by time slot
  const groupedSchedule = timeSlots.map(slot => ({
    ...slot,
    entries: schedule.filter(s => s.slot_id === slot.slot_id)
  })).filter(slot => slot.entries.length > 0);

  const selectedMedicine = medicines.find(m => m.medicine_id === (editingEntry?.medicine_id || formData.medicine_id));
  
  return (
    <div className="p-4 pb-24 max-w-2xl mx-auto animate-fade-in" data-testid="schedule-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('weeklySchedule')}</h1>
        <button 
          onClick={() => setShowForm(true)}
          className="btn-primary"
          disabled={medicines.length === 0}
          data-testid="add-schedule-btn"
        >
          <Plus className="w-5 h-5" />
          {t('add')}
        </button>
      </div>
      
      {/* Schedule List */}
      {groupedSchedule.length === 0 ? (
        <div className="glass-card p-8 text-center" data-testid="no-schedule-msg">
          <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">{t('noSchedule')}</p>
          {medicines.length === 0 ? (
            <p className="text-zinc-500 text-sm">{t('noMedicines')}</p>
          ) : (
            <button 
              onClick={() => setShowForm(true)}
              className="btn-primary"
            >
              <Plus className="w-5 h-5" />
              {t('addToSchedule')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {groupedSchedule.map(slot => (
            <div key={slot.slot_id} className="glass-card p-4" data-testid={`schedule-slot-${slot.slot_id}`}>
              <div 
                className="flex items-center gap-3 mb-4 pb-4 border-b border-zinc-800 cursor-pointer"
                onClick={() => {
                  const el = document.querySelector(`[data-testid="timeslot-${slot.slot_id}"]`);
                  if (el) { el.scrollIntoView({ behavior: 'smooth' }); el.click(); }
                }}
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">{slot.name}</h3>
                  <p className="text-sm text-zinc-400">{slot.time}</p>
                </div>
              </div>
              
              <div className="space-y-3">
                {slot.entries.map(entry => (
                  <div 
                    key={entry.entry_id}
                    className="p-3 bg-zinc-800/50 rounded-xl"
                    data-testid={`schedule-entry-${entry.entry_id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{entry.medicine_name}</p>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEdit(entry)}
                          className="p-2 hover:bg-zinc-700 rounded-lg transition-colors text-zinc-400 hover:text-emerald-400"
                          data-testid={`edit-schedule-${entry.entry_id}`}
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry.entry_id)}
                          className="p-2 hover:bg-zinc-700 rounded-lg transition-colors text-zinc-400 hover:text-red-400"
                          data-testid={`delete-schedule-${entry.entry_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-400">
                      {formatDayDoses(entry.day_doses, entry.medicine_dosage)}
                    </p>
                    {entry.special_ordination && (
                      <div className="mt-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300">
                        <span className="font-medium">{t('specialOrdination')}: </span>
                        {entry.special_ordination.whole || 1}{entry.special_ordination.half > 0 ? '½' : ''} {t(medicines.find(m => m.medicine_id === entry.medicine_id)?.unit || 'piller')}
                        {' · '}
                        {entry.special_ordination.start_date && format(parseISO(entry.special_ordination.start_date), 'd. MMM', { locale })}
                        {entry.special_ordination.end_date && ` — ${format(parseISO(entry.special_ordination.end_date), 'd. MMM yyyy', { locale })}`}
                        {' · '}{t(entry.special_ordination.repeat)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Time Slots - clickable to edit time */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-4">{t('timeSlots')}</h2>
        <div className="grid grid-cols-2 gap-3">
          {timeSlots.map(slot => (
            <EditableTimeSlot 
              key={slot.slot_id} 
              slot={slot} 
              onSave={(s, newTime) => updateTimeSlot(s.slot_id, { name: s.name, time: newTime, order: s.order })} 
            />
          ))}
        </div>
      </div>
      
      {/* Add/Edit Schedule Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 overflow-y-auto" data-testid="schedule-form-modal">
          <div className="min-h-full flex items-start justify-center p-4 pt-6 pb-8">
            <div className="glass-card w-full max-w-md p-6 animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                {editingEntry ? (language === 'da' ? 'Rediger dosis' : 'Edit Dose') : t('addToSchedule')}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-zinc-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {!editingEntry && (
                <>
                  {/* 1. Select Medicine */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">{t('selectMedicine')}</label>
                    <select
                      value={formData.medicine_id}
                      onChange={e => setFormData(prev => ({ ...prev, medicine_id: e.target.value }))}
                      className="select-field"
                      required
                      data-testid="select-medicine"
                    >
                      <option value="">{t('selectMedicine')}...</option>
                      {medicines.map(med => (
                        <option key={med.medicine_id} value={med.medicine_id}>
                          {med.name} ({med.dosage})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* 2. Special Ordination button - directly under medicine select */}
                  {formData.medicine_id && (
                    <>
                      {formData.special_ordination ? (
                        <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                              <FileText className="w-4 h-4" />
                              {t('specialOrdination')}
                            </div>
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, special_ordination: null, slot_id: '' }))}
                              className="p-1 hover:bg-zinc-800 rounded text-zinc-500 hover:text-red-400"
                              data-testid="remove-ordination-btn"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <div className="text-sm text-zinc-300 space-y-1">
                            {(() => {
                              const ordSlot = timeSlots.find(s => s.slot_id === formData.slot_id);
                              return ordSlot ? <p>{t('ordSelectTimeOfDay')}: {ordSlot.name} ({ordSlot.time})</p> : null;
                            })()}
                            <p>{t('ordFrom')}: {formData.special_ordination.start_date ? format(parseISO(formData.special_ordination.start_date), 'd. MMM yyyy', { locale }) : '—'}</p>
                            {formData.special_ordination.end_date && (
                              <p>{t('ordTo')}: {format(parseISO(formData.special_ordination.end_date), 'd. MMM yyyy', { locale })}</p>
                            )}
                            <p>{t('ordRepeat')}: {t(formData.special_ordination.repeat)}</p>
                            <p>{language === 'da' ? 'Antal' : 'Dose'}: {formData.special_ordination.whole || 1}{formData.special_ordination.half > 0 ? `½` : ''} {t(medicines.find(m => m.medicine_id === formData.medicine_id)?.unit || 'piller')}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => { 
                              setOrdData({ ...formData.special_ordination, time_of_day_id: formData.slot_id, whole: formData.special_ordination.whole || 1, half: formData.special_ordination.half || 0 }); 
                              setShowOrdination(true); 
                            }}
                            className="mt-2 text-xs text-emerald-400 hover:underline"
                            data-testid="edit-ordination-btn"
                          >
                            {t('edit')}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => { 
                            setOrdData({ start_date: null, end_date: null, repeat: 'daily', time_of_day_id: '', whole: 1, half: 0 }); 
                            setShowOrdination(true); 
                          }}
                          className="w-full py-3 rounded-xl border-2 border-dashed border-zinc-700 text-zinc-400 hover:border-emerald-500/50 hover:text-emerald-400 transition-all flex items-center justify-center gap-2 text-sm"
                          data-testid="special-ordination-btn"
                        >
                          <FileText className="w-4 h-4" />
                          {t('specialOrdination')}
                        </button>
                      )}
                    </>
                  )}
                  
                  {/* 3. Normal flow: Time slot + Day doses (hidden when special ordination is active) */}
                  {!formData.special_ordination && (
                    <div>
                      <label className="block text-sm text-zinc-400 mb-2">{t('selectTimeSlot')}</label>
                      <select
                        value={formData.slot_id}
                        onChange={e => setFormData(prev => ({ ...prev, slot_id: e.target.value }))}
                        className="select-field"
                        required
                        data-testid="select-timeslot"
                      >
                        <option value="">{t('selectTimeSlot')}...</option>
                        {timeSlots.map(slot => (
                          <option key={slot.slot_id} value={slot.slot_id}>
                            {slot.name} ({slot.time})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
              
              {editingEntry && (
                <div className="p-3 bg-zinc-800/50 rounded-xl mb-2">
                  <p className="font-medium">{editingEntry.medicine_name}</p>
                  <p className="text-sm text-zinc-400">{editingEntry.slot_name} ({editingEntry.slot_time})</p>
                </div>
              )}

              {/* Day doses - only shown when NOT using special ordination */}
              {!formData.special_ordination && (formData.medicine_id || editingEntry) && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-2">
                    {language === 'da' ? 'Dosis per dag' : 'Dose per day'}
                  </label>
                  <DayDoseSelector
                    dayDoses={formData.day_doses}
                    onChange={dayDoses => setFormData(prev => ({ ...prev, day_doses: dayDoses }))}
                    language={language}
                    medicineDosage={selectedMedicine?.dosage || editingEntry?.medicine_dosage}
                  />
                </div>
              )}
              
              {/* Special Ordination section for editing mode */}
              {editingEntry && (
                <>
                  {formData.special_ordination ? (
                    <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
                          <FileText className="w-4 h-4" />
                          {t('specialOrdination')}
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, special_ordination: null }))}
                          className="p-1 hover:bg-zinc-800 rounded text-zinc-500 hover:text-red-400"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <div className="text-sm text-zinc-300 space-y-1">
                        <p>{t('ordFrom')}: {formData.special_ordination.start_date ? format(parseISO(formData.special_ordination.start_date), 'd. MMM yyyy', { locale }) : '—'}</p>
                        {formData.special_ordination.end_date && (
                          <p>{t('ordTo')}: {format(parseISO(formData.special_ordination.end_date), 'd. MMM yyyy', { locale })}</p>
                        )}
                        <p>{t('ordRepeat')}: {t(formData.special_ordination.repeat)}</p>
                        <p>{language === 'da' ? 'Antal' : 'Dose'}: {formData.special_ordination.whole || 1}{formData.special_ordination.half > 0 ? `½` : ''} {t(editingEntry?.medicine ? medicines.find(m => m.medicine_id === editingEntry.medicine_id)?.unit || 'piller' : 'piller')}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setOrdData({ ...formData.special_ordination, time_of_day_id: formData.slot_id || editingEntry?.slot_id || '', whole: formData.special_ordination.whole || 1, half: formData.special_ordination.half || 0 }); setShowOrdination(true); }}
                        className="mt-2 text-xs text-emerald-400 hover:underline"
                      >
                        {t('edit')}
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setOrdData({ start_date: null, end_date: null, repeat: 'daily', time_of_day_id: editingEntry?.slot_id || '', whole: 1, half: 0 }); setShowOrdination(true); }}
                      className="w-full py-3 rounded-xl border-2 border-dashed border-zinc-700 text-zinc-400 hover:border-emerald-500/50 hover:text-emerald-400 transition-all flex items-center justify-center gap-2 text-sm"
                      data-testid="special-ordination-btn-edit"
                    >
                      <FileText className="w-4 h-4" />
                      {t('specialOrdination')}
                    </button>
                  )}
                </>
              )}
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-secondary flex-1"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading || (!editingEntry && (!formData.medicine_id || (!formData.special_ordination && (!formData.slot_id || Object.keys(formData.day_doses).length === 0)) || (formData.special_ordination && !formData.slot_id)))}
                  className="btn-primary flex-1"
                  data-testid="save-schedule-btn"
                >
                  {loading ? t('loading') : t('save')}
                </button>
              </div>
            </form>
          </div>
          </div>
        </div>
      )}
      
      {/* Special Ordination Popup */}
      {showOrdination && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[60]" onClick={() => setShowOrdination(false)}>
          <div className="glass-card w-full max-w-sm p-6 animate-fade-in max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="ordination-modal">
            <h3 className="text-lg font-semibold mb-1">{t('specialOrdination')}</h3>
            <p className="text-sm text-zinc-400 mb-5">{language === 'da' ? 'Angiv tidspunkt, periode og gentagelse' : 'Set time of day, period and repeat'}</p>
            
            {/* Time of Day selection */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">
                <Clock className="w-3.5 h-3.5 inline mr-1" />
                {t('ordSelectTimeOfDay')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {timeSlots.map(slot => (
                  <button
                    key={slot.slot_id}
                    type="button"
                    onClick={() => setOrdData(prev => ({ ...prev, time_of_day_id: slot.slot_id }))}
                    className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                      ordData.time_of_day_id === slot.slot_id
                        ? 'bg-emerald-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                    data-testid={`ord-timeslot-${slot.slot_id}`}
                  >
                    {slot.name}
                    <span className="block text-xs opacity-70">{slot.time}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Start Date */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">
                <CalendarIcon className="w-3.5 h-3.5 inline mr-1" />
                {t('ordStartDate')}
              </label>
              <OrdDatePicker
                value={ordData.start_date}
                onChange={val => setOrdData(prev => ({ ...prev, start_date: val }))}
                locale={locale}
                testId="ord-start-date"
              />
            </div>
            
            {/* End Date */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">
                <CalendarIcon className="w-3.5 h-3.5 inline mr-1" />
                {t('ordEndDate')}
              </label>
              <OrdDatePicker
                value={ordData.end_date}
                onChange={val => setOrdData(prev => ({ ...prev, end_date: val }))}
                locale={locale}
                testId="ord-end-date"
              />
            </div>
            
            {/* Repeat */}
            <div className="mb-4">
              <label className="block text-sm text-zinc-400 mb-2">
                <Repeat className="w-3.5 h-3.5 inline mr-1" />
                {t('ordRepeat')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {['daily', 'weekly', 'biweekly', 'monthly'].map(interval => (
                  <button
                    key={interval}
                    type="button"
                    onClick={() => setOrdData(prev => ({ ...prev, repeat: interval }))}
                    className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all ${
                      ordData.repeat === interval
                        ? 'bg-emerald-500 text-white'
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                    data-testid={`ord-repeat-${interval}`}
                  >
                    {t(interval)}
                  </button>
                ))}
              </div>
            </div>

            {/* Dose per occurrence */}
            <div className="mb-6">
              <label className="block text-sm text-zinc-400 mb-2">
                {language === 'da' ? 'Antal per gang' : 'Dose per occurrence'}
              </label>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-zinc-500 mb-1">
                    {language === 'da' ? 'Hele' : 'Whole'}
                  </label>
                  <input
                    type="number"
                    value={ordData.whole}
                    onChange={e => setOrdData(prev => ({ ...prev, whole: Math.max(0, parseInt(e.target.value) || 0) }))}
                    className="input-field text-center py-2"
                    min="0"
                    data-testid="ord-whole-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-zinc-500 mb-1">
                    {language === 'da' ? 'Halve' : 'Half'}
                  </label>
                  <input
                    type="number"
                    value={ordData.half}
                    onChange={e => setOrdData(prev => ({ ...prev, half: Math.max(0, parseInt(e.target.value) || 0) }))}
                    className="input-field text-center py-2"
                    min="0"
                    data-testid="ord-half-input"
                  />
                </div>
              </div>
              {(() => {
                const selMed = medicines.find(m => m.medicine_id === (editingEntry?.medicine_id || formData.medicine_id));
                if (!selMed) return null;
                const match = selMed.dosage?.match(/(\d+(?:[.,]\d+)?)\s*(mg|g|mcg|µg)/i);
                if (!match) return null;
                const val = parseFloat(match[1].replace(',', '.'));
                const unit = match[2];
                const total = val * ((ordData.whole || 0) + (ordData.half || 0) * 0.5);
                return (
                  <p className="text-sm text-emerald-400 mt-2 text-center font-medium">
                    {total % 1 === 0 ? total : total.toFixed(1)}{unit}
                  </p>
                );
              })()}
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowOrdination(false)}
                className="btn-secondary flex-1"
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => {
                  setFormData(prev => ({ 
                    ...prev, 
                    special_ordination: { 
                      start_date: ordData.start_date, 
                      end_date: ordData.end_date, 
                      repeat: ordData.repeat,
                      whole: ordData.whole || 1,
                      half: ordData.half || 0
                    },
                    slot_id: ordData.time_of_day_id
                  }));
                  setShowOrdination(false);
                }}
                disabled={!ordData.start_date || !ordData.time_of_day_id}
                className="btn-primary flex-1"
                data-testid="save-ordination-btn"
              >
                {t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
